import { format } from "date-fns";
import { useLocation } from "react-router-dom";

import { cn } from "@/utils/cn";

import { useArtifactDrawer } from "./artifactDrawerContext";
import { StatusPill } from "./StatusPill";

const ROUTE_LABELS: Record<string, string> = {
  "/chat": "Chat",
  "/history": "History",
  "/library": "Library",
  "/profile": "Profile",
};

interface Props {
  onOpenMobile?: () => void;
  date?: Date;
}

export function Masthead({ onOpenMobile, date = new Date() }: Props) {
  const { pathname } = useLocation();
  const matchKey = Object.keys(ROUTE_LABELS).find((k) => pathname.startsWith(k));
  const pageLabel = matchKey ? ROUTE_LABELS[matchKey] : "";
  const formatted = format(date, "EEEE d MMMM yyyy");
  const { artifacts, isOpen, toggle } = useArtifactDrawer();

  return (
    <header className="relative z-[60] h-masthead flex-none border-b border-ruleline bg-parchment-100">
      <div className="flex h-full items-center justify-between gap-4 px-6">
        <div className="flex items-center gap-3">
          {onOpenMobile ? (
            <button
              type="button"
              onClick={onOpenMobile}
              className="-ml-2 rounded-chip p-2 text-warmgray-500 transition-colors duration-200 ease-atelier hover:text-oxblood-500 lg:hidden"
              aria-label="Open menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
          ) : null}
          <span className="font-serif text-lg text-espresso-700">{pageLabel}</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Only offered once a run has actually produced something. */}
          {artifacts.length > 0 ? (
            <button
              type="button"
              onClick={toggle}
              aria-expanded={isOpen}
              className={cn(
                "inline-flex h-8 items-center gap-2 rounded-pill border border-oxblood-300 px-3",
                "font-sans text-[0.72rem] transition duration-200 ease-atelier",
                "hover:-translate-y-px active:translate-y-0 active:scale-[0.97]",
                isOpen
                  ? "bg-oxblood-500 text-parchment-50"
                  : "bg-parchment-50 text-oxblood-500 hover:bg-oxblood-50",
              )}
            >
              Artifacts
              <span
                className={cn(
                  "font-mono text-[0.66rem]",
                  isOpen ? "text-parchment-200" : "text-oxblood-400",
                )}
              >
                {artifacts.length}
              </span>
            </button>
          ) : null}

          <StatusPill className="hidden sm:inline-flex" />

          <div className="text-right text-[0.65rem] uppercase tracking-[0.18em] text-warmgray-400">
            {formatted}
          </div>
        </div>
      </div>
    </header>
  );
}
